import { fetchDistricts, fetchDistrictById, createDistrict } from '../api/districtApi';
import { District, DistrictQueryParams, DistrictListResponse, DistrictMeta } from '../types/district';
import ApiResponse from '@core/types/apiResponse';
import { createListService, makeServiceErrorResponse } from '@core/services/createListService';
import { formatDateOnly } from '@core/utils/formatDateOnly';

/**
 * Service layer for district-related business logic and data processing.
 * The list flow (validate / fetch / process) is assembled with the shared
 * `createListService` factory and consumed by `usePaginatedList`.
 */

/**
 * Validates district query parameters
 * @param params - Query parameters to validate
 * @returns Validation result with processed parameters
 */
export const validateDistrictQueryParams = (params: DistrictQueryParams): {
  isValid: boolean;
  processedParams: DistrictQueryParams;
  errors: string[]
} => {
  const errors: string[] = [];
  const processedParams = { ...params };

  // Validate pagination
  if (params.perPage && (params.perPage < 1 || params.perPage > 100)) {
    errors.push('Per page must be between 1 and 100');
    processedParams.perPage = Math.max(1, Math.min(100, params.perPage || 10));
  }

  if (params.page && params.page < 1) {
    errors.push('Page must be greater than 0');
    processedParams.page = 1;
  }

  // Validate search parameters
  if (params.districtName && params.districtName.length < 2) {
    errors.push('District name search must be at least 2 characters');
  }

  if (params.districtId && params.districtId <= 0) {
    errors.push('Invalid district ID');
  }

  return {
    isValid: errors.length === 0,
    processedParams,
    errors
  };
};

/** Adds computed display fields to a district row. */
const mapDistrictForDisplay = (district: District): District => ({
  ...district,
  displayName: district.district_name,
  isActive: district.status === 'active',
  memberCountDisplay: district.member_count ? `${district.member_count} members` : 'No data',
  establishedDateDisplay: formatDateOnly(district.established_date) ?? 'Not specified',
  contactDisplay: district.email || district.phone
    ? `${district.email || ''} ${district.phone || ''}`.trim()
    : 'No contact info',
} as District);

export const districtListService = createListService<District, DistrictQueryParams, DistrictMeta, DistrictListResponse>({
  fetchPage: fetchDistricts,
  validateParams: validateDistrictQueryParams,
  mapRow: mapDistrictForDisplay,
  filterKeys: ['freetext', 'districtName', 'churchName'],
  errorTitle: 'District Service Error',
});

/**
 * Fetches districts with business logic for parameter processing and error handling
 * @param params - Query parameters for filtering and pagination
 */
export const fetchDistrictsAsync = districtListService.fetchAsync;

/**
 * Processes district data for display (business logic)
 * @param districts - Array of district data
 */
export const processDistrictsForDisplay = districtListService.processForDisplay;

/**
 * Fetches a single district by ID with business logic
 * @param id - District ID
 * @returns Promise<ApiResponse<District>>
 */
export const fetchDistrictByIdAsync = async (id: number): Promise<ApiResponse<District>> => {
  try {
    if (!id || id <= 0) {
      throw new Error('Invalid district ID provided');
    }
    return await fetchDistrictById(id);
  } catch (error) {
    return makeServiceErrorResponse<District>(error, 'District Service Error');
  }
};

/**
 * Creates a new district with business logic
 * @param nationId - The ID of the nation
 * @param districtData - District creation data
 * @returns Promise<ApiResponse<District>>
 */
export const createDistrictAsync = async (
  nationId: number,
  districtData: { name: string; description: string }
): Promise<ApiResponse<District>> => {
  try {
    if (!nationId || nationId <= 0) {
      throw new Error('Invalid nation ID provided');
    }

    if (!districtData.name || districtData.name.trim().length < 2) {
      throw new Error('District name must be at least 2 characters long');
    }

    return await createDistrict(nationId, districtData);
  } catch (error) {
    return makeServiceErrorResponse<District>(error, 'District Service Error');
  }
};

/**
 * Gets district status information
 * @param district - District data
 * @returns Status information
 */
export const getDistrictStatusInfo = (district: District) => {
  const statusMap = {
    active: { variant: 'success', text: 'Active' },
    inactive: { variant: 'secondary', text: 'Inactive' },
    pending: { variant: 'warning', text: 'Pending' }
  };

  const status = district.status || 'unknown';
  return statusMap[status as keyof typeof statusMap] || { variant: 'info', text: 'Unknown' };
};
